/**
 * From the Founder — Tom Vellaringattu's essay on why NaturePulse exists.
 *
 * Editorial long-form layout. Kept intentionally text-heavy and quiet:
 * the story is the design. Big display headline, circular portrait, a
 * pull-quote treatment for the guiding sentence, and a chapter-styled
 * section structure. Uses the site's existing `--wofy-*` CSS tokens so
 * it inherits the naturepulse accent when rendered on naturepulse.net.
 */
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Leaf, Camera, Globe2, Sprout } from "lucide-react";

const FOUNDER_PHOTO =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/v6d0djs1_tomvell-fp-photo.jpg";

const CHAPTERS = [
  {
    id: "from-village",
    title: "From a village in Kerala to a living record of our planet",
    body: [
      "My fascination with nature began long before smartphones, digital photography or the Internet.",
      "I grew up in Elivaly, in Kadanad village in Kottayam district, Kerala, India, surrounded by the rhythms of rural life. As a child, I spent a great deal of time with my grandfather and father planting, tending and nurturing plants — some for our own food and others grown as agricultural crops for trade.",
      "Those early experiences gave me an appreciation for something very simple: nature is constantly changing, and if we pay attention, it is constantly telling us something.",
    ],
  },
  {
    id: "lifelong",
    title: "A lifelong fascination with nature",
    body: [
      "Plants and flowers have remained among my favorite subjects throughout my life. Whenever I am hiking a trail, walking through a neighborhood or simply wandering along a city street, I notice what is growing around me.",
    ],
    listLead: "A flower blooming. A tree changing color. A bird building a nest.",
    bullets: [
      "A mushroom appearing after rain.",
      "Clouds gathering over a mountain.",
      "A dramatic sunset.",
      "An unusual insect.",
      "A stream running higher or lower than usual.",
    ],
    bodyAfter: [
      "My phone seems to jump out of my pocket automatically. I photograph what I see. Over the years, this habit has resulted in a personal collection of more than 300,000 photographs.",
      "But eventually I began asking myself: what should I do with all these photographs? Simply storing them on a phone or computer seemed like a missed opportunity.",
      "Many of these photographs are more than memories. They represent a particular plant, animal, landscape, weather condition, season, place and moment in time. Collectively, photographs like these can tell a much larger story.",
    ],
  },
  {
    id: "idea",
    title: "The idea behind NaturePulse",
    body: [
      "That realization led me to create NaturePulse.net.",
      "NaturePulse is intended to become a worldwide platform where ordinary people, nature enthusiasts, photographers, students, scientists and communities can observe, photograph and document our living planet.",
    ],
    bullets: [
      "Someone in Kerala may photograph the first flowering of a mango tree.",
      "Someone in Oregon may document an unusually early wildflower bloom.",
      "A farmer in Africa may photograph changing rainfall conditions.",
      "A student in Indonesia may record coastal flooding.",
      "A traveler may photograph disappearing snow on a mountain.",
      "A birdwatcher may notice that a species once common in a neighborhood has become increasingly difficult to find.",
    ],
    bodyAfter: [
      "One observation by itself may seem insignificant. Thousands or millions of observations, collected over many years and locations, can become something much more valuable.",
    ],
  },
  {
    id: "pulse",
    title: "Taking the pulse of nature",
    body: [
      "Climate change is often discussed through global statistics — temperatures, atmospheric carbon dioxide, sea levels and satellite measurements. Those measurements are extremely important.",
      "But environmental change is also happening right outside our doors. We can see it in flowering times, bird populations, insects, rainfall, drought, flooding, erosion, changing coastlines, disappearing wetlands, forest health, agricultural conditions and countless other signals.",
      "NaturePulse encourages people to become observers of these changes. You do not have to be a scientist. You simply have to look, photograph, document and share what you see.",
    ],
  },
  {
    id: "repository",
    title: "A global citizen nature repository",
    body: [
      "My hope is that NaturePulse will gradually develop into a large, searchable historical repository of observations about:",
    ],
    bullets: [
      "Plants, flowers and vegetation",
      "Trees and forests",
      "Agriculture and edible plants",
      "Birds and nesting activity",
      "Animals and wildlife",
      "Insects and pollinators",
      "Fish and aquatic life",
      "Rivers, lakes, wetlands and oceans",
      "Mountains, hills and landscapes",
      "Clouds and atmospheric conditions",
      "Rain, snow, storms and drought",
      "Flooding, erosion and landslides",
      "Sunrise, sunset and seasonal changes",
      "Habitats and ecosystems",
      "Pollution and environmental damage",
      "Climate-related changes",
      "Conservation and restoration efforts",
    ],
    bodyAfter: [
      "Each observation becomes another small piece of the enormous story of our planet.",
    ],
  },
  {
    id: "photography",
    title: "Photography with purpose",
    body: [
      "Nature photography is beautiful, but it can also have purpose.",
      "A photograph becomes considerably more useful when we know what it shows, where it was taken, when it was taken and what was happening around it.",
      "NaturePulse is therefore being developed not simply as a photo gallery, but as a platform for structured observation. With appropriate tagging, dates, locations, descriptions and categories, today's casual photograph could become tomorrow's historical environmental record.",
      "Imagine being able to look at photographs of the same tree, lake, coastline, neighborhood or forest over 10, 20 or 50 years. That is the potential of collective documentation.",
    ],
  },
  {
    id: "future",
    title: "A resource for future generations",
    body: [
      "I believe a sufficiently large NaturePulse repository could eventually become useful to:",
    ],
    bullets: [
      "Students exploring nature in their communities.",
      "Teachers introducing children to environmental observation.",
      "Researchers studying ecological and environmental changes.",
      "Farmers and gardeners tracking seasonal and agricultural patterns.",
      "Conservation organizations documenting habitats and biodiversity.",
      "Local communities recording changes to their environment.",
      "Governments and planners understanding environmental conditions at the local level.",
    ],
    bodyAfter: [
      "And perhaps most importantly, future generations who want to understand what particular places looked like decades earlier.",
    ],
  },
  {
    id: "tools",
    title: "Free tools for everyone",
    body: [
      "NaturePulse includes tools for documenting observations, organizing photographs, publishing articles and sharing knowledge. I want these basic capabilities to remain accessible so that participation is not limited to professional researchers or institutions.",
      "Nature belongs to everyone. The opportunity to document it should belong to everyone as well.",
    ],
  },
  {
    id: "neighborhood",
    title: "Your neighborhood matters",
    body: [
      "You don't have to travel to the Amazon rainforest, the Himalayas or the African savanna to contribute something meaningful.",
    ],
    listLead: "Start with your backyard.",
    bullets: [
      "Your street.",
      "Your neighborhood park.",
      "Your farm.",
      "Your village.",
      "Your hiking trail.",
      "Your river.",
      "Your beach.",
      "Your city.",
    ],
    bodyAfter: [
      "Photograph what you notice today. Come back next week, next season and next year. Over time, those observations create a record.",
    ],
  },
  {
    id: "help",
    title: "Help us document our living planet",
    body: [
      "NaturePulse is an evolving project, and this is only the beginning. I invite nature enthusiasts, photographers, gardeners, farmers, students, teachers, researchers and curious people everywhere to participate.",
      "Observe something. Photograph it. Identify it. Describe it. Tag it. Share it.",
      "Together, millions of small observations can help us create an extraordinary record of the changing natural world.",
    ],
  },
];


function Chapter({ ch, index }) {
  return (
    <section
      id={ch.id}
      data-testid={`founder-chapter-${ch.id}`}
      className="mt-14 md:mt-20 scroll-mt-24"
    >
      <div className="flex items-baseline gap-4 mb-4">
        <span
          className="font-mono text-xs tracking-[0.2em] opacity-40"
          style={{ color: "var(--wofy-text-soft)" }}
        >
          CH · {String(index + 1).padStart(2, "0")}
        </span>
        <div
          className="flex-1 h-px"
          style={{ background: "var(--wofy-border)" }}
        />
      </div>
      <h2 className="font-display text-3xl md:text-4xl leading-[1.1] mb-6"
          style={{ color: "var(--wofy-text)" }}>
        {ch.title}
      </h2>
      {ch.body?.map((p, i) => (
        <p key={i} className="mt-4 text-[17px] leading-[1.75]"
           style={{ color: "var(--wofy-text)" }}>
          {p}
        </p>
      ))}
      {ch.listLead && (
        <p className="mt-6 text-[17px] leading-[1.75] font-medium italic"
           style={{ color: "var(--wofy-text)" }}>
          {ch.listLead}
        </p>
      )}
      {ch.bullets && (
        <ul className="mt-3 space-y-2">
          {ch.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-[16px] leading-[1.6]"
                style={{ color: "var(--wofy-text)" }}>
              <Leaf className="w-3.5 h-3.5 mt-1.5 flex-none"
                    style={{ color: "#0E7C55" }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {ch.bodyAfter?.map((p, i) => (
        <p key={i} className="mt-4 text-[17px] leading-[1.75]"
           style={{ color: "var(--wofy-text)" }}>
          {p}
        </p>
      ))}
    </section>
  );
}


export default function NatureFromTheFounder() {
  return (
    <div
      data-testid="naturepulse-from-founder"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet>
        <title>From the Founder · Why I Created NaturePulse — Tom Vellaringattu</title>
        <meta
          name="description"
          content="Tom (Thomas) Vellaringattu on why he created NaturePulse.net — from a village in Kerala to a global citizen-science record of our living planet."
        />
        <link rel="canonical" href="https://naturepulse.net/nature/from-the-founder" />
        <meta property="og:title" content="From the Founder — Why I Created NaturePulse" />
        <meta property="og:description" content="From a village in Kerala to a living record of our planet." />
        <meta property="og:image" content={FOUNDER_PHOTO} />
      </Helmet>

      <article className="max-w-[860px] mx-auto px-5 md:px-8 lg:px-10 py-12 md:py-20">
        {/* Breadcrumb */}
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] mb-8 opacity-60">
          <Link to="/" className="hover:opacity-100">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/nature" className="hover:opacity-100">NaturePulse</Link>
          <span className="mx-2">/</span>
          <span>From the Founder</span>
        </div>

        {/* Eyebrow */}
        <div className="flex items-center gap-2 mb-5">
          <Sprout className="w-4 h-4" style={{ color: "#0E7C55" }} />
          <span
            className="font-mono uppercase text-[11px] tracking-[0.2em]"
            style={{ color: "#0E7C55" }}
          >
            From the Founder
          </span>
        </div>

        {/* Display headline */}
        <h1
          data-testid="founder-heading"
          className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight"
          style={{ color: "var(--wofy-text)" }}
        >
          Why I created <em style={{ color: "#0E7C55", fontStyle: "italic" }}>NaturePulse</em>
        </h1>

        {/* Kicker */}
        <p
          className="mt-6 text-lg md:text-xl leading-[1.5]"
          style={{ color: "var(--wofy-text-soft)" }}
        >
          From a village in Kerala to a living record of our planet — an
          invitation to observe, document and share the world outside
          your door.
        </p>

        {/* Byline block with portrait */}
        <div
          data-testid="founder-byline"
          className="mt-10 flex items-center gap-5 pb-8 border-b"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <img
            src={FOUNDER_PHOTO}
            alt="Tom (Thomas) Vellaringattu"
            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-none shadow-sm ring-2 ring-white"
            style={{ boxShadow: "0 4px 16px rgba(14, 124, 85, 0.15)" }}
          />
          <div className="min-w-0">
            <div
              className="font-display text-[17px] leading-tight"
              style={{ color: "var(--wofy-text)" }}
            >
              Tom (Thomas) Vellaringattu
            </div>
            <div
              className="text-[13px] mt-0.5"
              style={{ color: "var(--wofy-text-soft)" }}
            >
              Founder, NaturePulse.net · A division of Social Pulse Inc.
            </div>
          </div>
        </div>

        {/* Body */}
        {CHAPTERS.slice(0, 2).map((ch, i) => (
          <Chapter key={ch.id} ch={ch} index={i} />
        ))}

        {/* Pull-quote treatment for the guiding sentence */}
        <aside
          data-testid="founder-pullquote"
          className="my-14 md:my-20 py-10 md:py-14 px-6 md:px-10 rounded-sm relative"
          style={{
            background: "linear-gradient(180deg, rgba(14,124,85,0.06) 0%, rgba(14,124,85,0.02) 100%)",
            borderLeft: "3px solid #0E7C55",
          }}
        >
          <Globe2
            className="absolute top-6 right-6 w-5 h-5 opacity-30"
            style={{ color: "#0E7C55" }}
          />
          <div
            className="font-mono text-[10px] uppercase tracking-[0.24em] mb-4"
            style={{ color: "#0E7C55" }}
          >
            The idea, in one line
          </div>
          <blockquote
            className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] italic"
            style={{ color: "var(--wofy-text)" }}
          >
            Observe&nbsp;locally.
            <br className="hidden sm:block" />
            Document&nbsp;regularly.
            <br className="hidden sm:block" />
            Share&nbsp;globally.
          </blockquote>
        </aside>

        {CHAPTERS.slice(2).map((ch, i) => (
          <Chapter key={ch.id} ch={ch} index={i + 2} />
        ))}

        {/* Signature */}
        <div
          className="mt-16 md:mt-24 pt-8 border-t"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <div className="flex items-start gap-5">
            <img
              src={FOUNDER_PHOTO}
              alt="Tom Vellaringattu"
              className="w-14 h-14 rounded-full object-cover flex-none"
            />
            <div>
              <p
                className="font-display italic text-2xl leading-tight"
                style={{ color: "var(--wofy-text)" }}
              >
                — Tom (Thomas) Vellaringattu
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: "var(--wofy-text-soft)" }}
              >
                Founder, NaturePulse.net
              </p>
            </div>
          </div>
        </div>

        {/* Tagline coda */}
        <p
          className="mt-14 text-center font-display text-lg md:text-xl italic"
          style={{ color: "#0E7C55" }}
        >
          NaturePulse.net — Documenting the pulse of our living planet,
          one observation at a time.
        </p>

        {/* CTA row */}
        <div
          data-testid="founder-cta-row"
          className="mt-14 grid gap-3 sm:grid-cols-2"
        >
          <Link
            to="/nature/identify"
            data-testid="founder-cta-observe"
            className="wofy-btn-primary flex items-center justify-center gap-2 py-3"
          >
            <Camera className="w-4 h-4" /> Post your first observation
          </Link>
          <Link
            to="/nature"
            data-testid="founder-cta-explore"
            className="wofy-btn-ghost flex items-center justify-center gap-2 py-3"
          >
            <Leaf className="w-4 h-4" /> Explore NaturePulse
          </Link>
        </div>

        {/* Back link */}
        <Link
          to="/nature"
          className="mt-10 inline-flex items-center gap-1.5 text-sm underline opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="w-4 h-4" /> Back to NaturePulse
        </Link>
      </article>
    </div>
  );
}
